import React from "react";

import {
	AvatarGroup,
	Chip,
	type ICellRendererParams,
	Link,
	type PYColumnDef,
	type SortChangedEvent,
	Table,
	Text,
} from "@performyard/py-components";
import { MeetingExtendedModelJson } from "@performyard/service-contracts";

import { MEETING_BREADCRUMB_SOURCE, appendMeetingBreadcrumbSource } from "../../Meetings/utils/breadcrumbSource";
import { getFormattedMeetingDateString } from "../utils";

interface CompanyMeetingsTableProps {
	meetings: MeetingExtendedModelJson[];
	isFetchingMeetings: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
}

const NameCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	return (
		<div className="flex h-full w-full min-w-0 items-center">
			<Link
				href={appendMeetingBreadcrumbSource(
					`/meeting-workspaces/${meeting.meetingWorkspaceId}/meetings/${meeting._id}`,
					MEETING_BREADCRUMB_SOURCE.companyMeetings
				)}
				forceHardReload
				underline="hover"
			>
				<Text
					size="paragraph-3"
					weight="regular"
					dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
				>
					{meeting.name}
				</Text>
			</Link>
		</div>
	);
};

const ParticipantsCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	const employees =
		meeting.participants?.map((participant) => ({
			_id: participant.userId,
			name: participant.name,
		})) ?? [];
	return (
		<div className="flex h-full w-full min-w-0 items-center">
			<AvatarGroup
				employees={employees}
				maxLength={employees.length <= 5 ? 5 : 4}
				employeeNameTooltip={true}
				hasLinks={true}
				modalTitle="Workspace Participants"
				modalSubtitle={meeting.name}
			/>
		</div>
	);
};

const WorkspaceNameCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	return (
		<div className="flex h-full w-full min-w-0 items-center">
			<Link
				href={appendMeetingBreadcrumbSource(
					`/meeting-workspaces/${meeting.meetingWorkspaceId}`,
					MEETING_BREADCRUMB_SOURCE.companyMeetings
				)}
				forceHardReload
				underline="hover"
			>
				<Text
					size="paragraph-3"
					weight="regular"
					dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
				>
					{meeting.meetingWorkspaceName || ""}
				</Text>
			</Link>
		</div>
	);
};

const MeetingDateCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	return (
		<div className="flex h-full w-full min-w-0 items-center">
			<Text
				size="paragraph-3"
				weight="regular"
				dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
			>
				{getFormattedMeetingDateString(meeting.startDateTime, meeting.endDateTime)}
			</Text>
		</div>
	);
};

const StatusCellRenderer = (params: ICellRendererParams<MeetingExtendedModelJson>) => {
	const meeting = params.data;
	if (!meeting) return null;
	let statusChip: React.ReactNode;
	switch (meeting.status) {
		case "active":
			statusChip = <Chip label="Active" color="primary" size={20} />;
			break;
		case "archived":
			statusChip = <Chip label="Archived" color="neutral" size={20} />;
			break;
		case "completed":
			statusChip = <Chip label="Completed" color="green" size={20} />;
			break;
		case "canceled":
			statusChip = <Chip label="Canceled" color="red" size={20} />;
			break;
		default:
			statusChip = <Chip label={meeting.status} color="primary" size={20} />;
	}
	return <div className="flex h-full w-full min-w-0 items-center">{statusChip}</div>;
};

const columnDefs: PYColumnDef<MeetingExtendedModelJson>[] = [
	{
		field: "name",
		headerName: "Meeting Name",
		sortable: true,
		resizable: false,
		width: 288,
		minWidth: 240,
		cellClass: "flex items-center",
		cellRenderer: NameCellRenderer,
	},
	{
		field: "participants",
		headerName: "Participants",
		sortable: false,
		resizable: false,
		width: 106,
		minWidth: 106,
		maxWidth: 106,
		cellClass: "flex items-center",
		cellRenderer: ParticipantsCellRenderer,
	},
	{
		field: "meetingWorkspaceName",
		headerName: "Meeting Workspace Name",
		sortable: true,
		resizable: false,
		width: 288,
		minWidth: 240,
		cellClass: "flex items-center",
		cellRenderer: WorkspaceNameCellRenderer,
	},
	{
		field: "meetingDate",
		headerName: "Meeting Date",
		sortable: true,
		resizable: false,
		width: 272,
		minWidth: 260,
		cellClass: "flex items-center",
		cellRenderer: MeetingDateCellRenderer,
	},
	{
		field: "status",
		headerName: "Status",
		sortable: true,
		resizable: false,
		width: 140,
		minWidth: 140,
		cellClass: "flex items-center",
		cellRenderer: StatusCellRenderer,
	},
];

const CompanyMeetingsTable = (props: CompanyMeetingsTableProps) => {
	const { meetings, isFetchingMeetings, activeSort, activeSortDirection, handleColumnHeaderClick } = props;

	const onSortChanged = (event: SortChangedEvent<MeetingExtendedModelJson>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="companyMeetingsContainer">
			<Table<MeetingExtendedModelJson>
				autoHeight
				columnSizing="fit-grid"
				rowData={meetings}
				columnDefs={columnDefs}
				loading={isFetchingMeetings}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Meetings Found"
				initialState={{
					sort: {
						sortModel: activeSort
							? [{ colId: activeSort, sort: activeSortDirection as "asc" | "desc" }]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default CompanyMeetingsTable;
